/**
 * AYU Check-In Schema – Kurzer Fragebogen für den aktuellen funktionalen Zustand
 *
 * Wird alle 3-4 Monate ausgefüllt, um den dynamischen Zustand (Vikriti) zu erfassen.
 * Der Kerntyp (Prakriti) aus dem Onboarding bleibt stabil.
 *
 * 18 Fragen in 5 Kategorien:
 *   1. Energie & Vitalität (3 Fragen)
 *   2. Verdauung & Stoffwechsel (3 Fragen)
 *   3. Schlaf & Erholung (3 Fragen)
 *   4. Stress & Emotionen (4 Fragen)
 *   5. Körper & Haut (3 Fragen)
 *   6. Saisonale Faktoren (2 Fragen)
 */

export interface CheckInQuestion {
  id: string;
  category: string;
  text: string;
  type: "radio" | "range" | "multiselect";
  options?: { value: string; label: string; score?: Record<string, number> }[];
  min?: number;
  max?: number;
  step?: number;
  minLabel?: string;
  maxLabel?: string;
}

export interface CheckInAnswer {
  questionId: string;
  value: string | number | string[];
}

export interface FunctionalState {
  /** Energie-Level (1-10) */
  energy: number;
  /** Verdauungsqualität (1-10) */
  digestion: number;
  /** Schlafqualität (1-10) */
  sleep: number;
  /** Stresslevel (1-10, 10 = sehr gestresst) */
  stress: number;
  /** Emotionale Balance (1-10) */
  emotionalBalance: number;
  /** Hautgesundheit (1-10) */
  skinHealth: number;
  /** Immunstärke (1-10) */
  immunity: number;
  /** Dominante aktuelle Imbalance */
  dominantImbalance: string;
  /** Saisonaler Einfluss */
  seasonalFactor: string;
  /** Zusammenfassung */
  summary: string;
  /** Zeitpunkt der Erfassung */
  capturedAt: number;
}

// ─── CHECK-IN FRAGEN ───

export const CHECK_IN_QUESTIONS: CheckInQuestion[] = [
  // ── Energie & Vitalität ──
  {
    id: "ci_energy_level",
    category: "Energie & Vitalität",
    text: "Wie würdest du dein aktuelles Energielevel beschreiben?",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    minLabel: "Sehr niedrig",
    maxLabel: "Voller Energie",
  },
  {
    id: "ci_energy_pattern",
    category: "Energie & Vitalität",
    text: "Wann fühlst du dich aktuell am energiegeladensten?",
    type: "radio",
    options: [
      { value: "morning", label: "Morgens (6-10 Uhr)" },
      { value: "midday", label: "Mittags (10-14 Uhr)" },
      { value: "afternoon", label: "Nachmittags (14-18 Uhr)" },
      { value: "evening", label: "Abends (18-22 Uhr)" },
      { value: "inconsistent", label: "Schwankt stark / kein Muster" },
    ],
  },
  {
    id: "ci_fatigue",
    category: "Energie & Vitalität",
    text: "Wie oft fühlst du dich aktuell müde oder erschöpft?",
    type: "radio",
    options: [
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal (1-2x pro Woche)" },
      { value: "often", label: "Oft (3-5x pro Woche)" },
      { value: "daily", label: "Täglich" },
    ],
  },

  // ── Verdauung & Stoffwechsel ──
  {
    id: "ci_digestion_quality",
    category: "Verdauung & Stoffwechsel",
    text: "Wie ist deine Verdauung aktuell?",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    minLabel: "Sehr problematisch",
    maxLabel: "Ausgezeichnet",
  },
  {
    id: "ci_digestion_issues",
    category: "Verdauung & Stoffwechsel",
    text: "Welche Verdauungsprobleme hast du aktuell?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine Probleme" },
      { value: "bloating", label: "Blähungen" },
      { value: "constipation", label: "Verstopfung" },
      { value: "diarrhea", label: "Durchfall" },
      { value: "acid_reflux", label: "Sodbrennen / Reflux" },
      { value: "heaviness", label: "Schweregefühl nach dem Essen" },
      { value: "appetite_low", label: "Wenig Appetit" },
      { value: "appetite_high", label: "Übermäßiger Hunger" },
    ],
  },
  {
    id: "ci_appetite",
    category: "Verdauung & Stoffwechsel",
    text: "Wie ist dein Appetit aktuell?",
    type: "radio",
    options: [
      { value: "low", label: "Gering — esse wenig, kein Hunger" },
      { value: "normal", label: "Normal — regelmäßig und angemessen" },
      { value: "high", label: "Stark — ständig hungrig" },
      { value: "irregular", label: "Unregelmäßig — schwankt stark" },
    ],
  },

  // ── Schlaf & Erholung ──
  {
    id: "ci_sleep_quality",
    category: "Schlaf & Erholung",
    text: "Wie bewertest du deine aktuelle Schlafqualität?",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    minLabel: "Sehr schlecht",
    maxLabel: "Ausgezeichnet",
  },
  {
    id: "ci_sleep_issues",
    category: "Schlaf & Erholung",
    text: "Welche Schlafprobleme hast du aktuell?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine Probleme" },
      { value: "falling_asleep", label: "Einschlafprobleme" },
      { value: "staying_asleep", label: "Durchschlafprobleme" },
      { value: "early_waking", label: "Zu frühes Aufwachen" },
      { value: "not_rested", label: "Wache unerholt auf" },
      { value: "racing_mind", label: "Gedankenkarussell" },
    ],
  },
  {
    id: "ci_sleep_hours",
    category: "Schlaf & Erholung",
    text: "Wie viele Stunden schläfst du durchschnittlich pro Nacht?",
    type: "radio",
    options: [
      { value: "under5", label: "Unter 5 Stunden" },
      { value: "5to6", label: "5-6 Stunden" },
      { value: "6to7", label: "6-7 Stunden" },
      { value: "7to8", label: "7-8 Stunden" },
      { value: "over8", label: "Über 8 Stunden" },
    ],
  },

  // ── Stress & Emotionen ──
  {
    id: "ci_stress_level",
    category: "Stress & Emotionen",
    text: "Wie hoch ist dein aktuelles Stresslevel?",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    minLabel: "Sehr entspannt",
    maxLabel: "Extrem gestresst",
  },
  {
    id: "ci_stress_sources",
    category: "Stress & Emotionen",
    text: "Was sind aktuell deine Hauptstressquellen?",
    type: "multiselect",
    options: [
      { value: "work", label: "Arbeit / Beruf" },
      { value: "family", label: "Familie / Beziehung" },
      { value: "health", label: "Gesundheitliche Sorgen" },
      { value: "finances", label: "Finanzen" },
      { value: "social", label: "Soziale Situationen" },
      { value: "loneliness", label: "Einsamkeit" },
      { value: "uncertainty", label: "Unsicherheit / Zukunftsangst" },
      { value: "none", label: "Kein besonderer Stress" },
    ],
  },
  {
    id: "ci_mood",
    category: "Stress & Emotionen",
    text: "Wie würdest du deine aktuelle Stimmung beschreiben?",
    type: "radio",
    options: [
      { value: "very_positive", label: "Sehr positiv und ausgeglichen" },
      { value: "positive", label: "Überwiegend positiv" },
      { value: "neutral", label: "Neutral / wechselhaft" },
      { value: "low", label: "Eher niedergeschlagen" },
      { value: "anxious", label: "Ängstlich / unruhig" },
      { value: "irritable", label: "Gereizt / ungeduldig" },
    ],
  },
  {
    id: "ci_emotional_balance",
    category: "Stress & Emotionen",
    text: "Wie gut kannst du aktuell mit emotionalen Herausforderungen umgehen?",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    minLabel: "Sehr schwer",
    maxLabel: "Sehr gut",
  },

  // ── Körper & Haut ──
  {
    id: "ci_skin_condition",
    category: "Körper & Haut",
    text: "Wie ist der aktuelle Zustand deiner Haut?",
    type: "radio",
    options: [
      { value: "great", label: "Sehr gut — klar und strahlend" },
      { value: "normal", label: "Normal — keine besonderen Auffälligkeiten" },
      { value: "dry", label: "Trocken / schuppig" },
      { value: "oily", label: "Fettig / glänzend" },
      { value: "breakouts", label: "Unreinheiten / Ausschläge" },
      { value: "sensitive", label: "Empfindlich / gerötet" },
    ],
  },
  {
    id: "ci_body_symptoms",
    category: "Körper & Haut",
    text: "Welche körperlichen Beschwerden hast du aktuell?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine besonderen Beschwerden" },
      { value: "headaches", label: "Kopfschmerzen" },
      { value: "joint_pain", label: "Gelenkschmerzen" },
      { value: "back_pain", label: "Rückenschmerzen" },
      { value: "muscle_tension", label: "Muskelverspannungen" },
      { value: "cold_hands_feet", label: "Kalte Hände / Füße" },
      { value: "sweating", label: "Übermäßiges Schwitzen" },
      { value: "allergies", label: "Allergien / Heuschnupfen" },
    ],
  },
  {
    id: "ci_immunity",
    category: "Körper & Haut",
    text: "Wie oft warst du in den letzten 3 Monaten krank (Erkältung, Grippe etc.)?",
    type: "radio",
    options: [
      { value: "never", label: "Gar nicht" },
      { value: "once", label: "Einmal" },
      { value: "twice", label: "Zweimal" },
      { value: "three_plus", label: "Dreimal oder öfter" },
    ],
  },

  // ── Saisonale Faktoren ──
  {
    id: "ci_season_effect",
    category: "Saisonale Faktoren",
    text: "Wie beeinflusst die aktuelle Jahreszeit dein Wohlbefinden?",
    type: "radio",
    options: [
      { value: "very_positive", label: "Sehr positiv — ich blühe auf" },
      { value: "neutral", label: "Kaum Einfluss" },
      { value: "challenging", label: "Herausfordernd — ich merke Veränderungen" },
      { value: "difficult", label: "Schwierig — ich kämpfe mit der Jahreszeit" },
    ],
  },
  {
    id: "ci_lifestyle_changes",
    category: "Saisonale Faktoren",
    text: "Hat sich in den letzten 3 Monaten etwas Wesentliches in deinem Leben verändert?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine wesentlichen Veränderungen" },
      { value: "job_change", label: "Jobwechsel / Berufliche Veränderung" },
      { value: "relationship", label: "Beziehungsveränderung" },
      { value: "move", label: "Umzug" },
      { value: "diet_change", label: "Ernährungsumstellung" },
      { value: "exercise_change", label: "Veränderung im Bewegungsverhalten" },
      { value: "health_event", label: "Gesundheitliches Ereignis" },
      { value: "loss", label: "Verlust / Trauer" },
    ],
  },
];

/**
 * Berechnet den funktionalen Zustand aus den Check-In Antworten.
 */
export function calculateFunctionalState(answers: CheckInAnswer[]): FunctionalState {
  const getAnswer = (id: string) => answers.find((a) => a.questionId === id);

  // Energie
  const energyLevel = Number(getAnswer("ci_energy_level")?.value) || 5;
  const fatigue = getAnswer("ci_fatigue")?.value;
  const fatigueModifier = fatigue === "daily" ? -2 : fatigue === "often" ? -1 : fatigue === "rarely" ? 1 : 0;
  const energy = Math.max(1, Math.min(10, energyLevel + fatigueModifier));

  // Verdauung
  const digestionQuality = Number(getAnswer("ci_digestion_quality")?.value) || 5;
  const digestionIssues = (getAnswer("ci_digestion_issues")?.value as string[]) || [];
  const hasNoDigestionIssues = digestionIssues.includes("none") || digestionIssues.length === 0;
  const digestion = hasNoDigestionIssues ? digestionQuality : Math.max(1, digestionQuality - digestionIssues.length * 0.5);

  // Schlaf
  const sleepQuality = Number(getAnswer("ci_sleep_quality")?.value) || 5;
  const sleepIssues = (getAnswer("ci_sleep_issues")?.value as string[]) || [];
  const hasNoSleepIssues = sleepIssues.includes("none") || sleepIssues.length === 0;
  const sleep = hasNoSleepIssues ? sleepQuality : Math.max(1, sleepQuality - sleepIssues.length * 0.5);

  // Stress (invertiert: hoher Wert = mehr Stress)
  const stressLevel = Number(getAnswer("ci_stress_level")?.value) || 5;
  const stress = stressLevel;

  // Emotionale Balance
  const emotionalBalanceRaw = Number(getAnswer("ci_emotional_balance")?.value) || 5;
  const mood = getAnswer("ci_mood")?.value;
  const moodModifier = mood === "very_positive" ? 1 : mood === "low" ? -1 : mood === "anxious" ? -1.5 : mood === "irritable" ? -1 : 0;
  const emotionalBalance = Math.max(1, Math.min(10, emotionalBalanceRaw + moodModifier));

  // Hautgesundheit
  const skinCondition = getAnswer("ci_skin_condition")?.value;
  const skinHealth = skinCondition === "great" ? 9 : skinCondition === "normal" ? 7 : skinCondition === "dry" ? 5 : skinCondition === "oily" ? 5 : skinCondition === "breakouts" ? 3 : skinCondition === "sensitive" ? 4 : 5;

  // Immunstärke
  const immunityAnswer = getAnswer("ci_immunity")?.value;
  const immunity = immunityAnswer === "never" ? 9 : immunityAnswer === "once" ? 7 : immunityAnswer === "twice" ? 5 : 3;

  // Dominante Imbalance bestimmen
  const scores: Record<string, number> = {
    "Energie-Mangel": 10 - energy,
    "Verdauungsschwäche": 10 - digestion,
    "Schlafstörung": 10 - sleep,
    "Stress-Überlastung": stress,
    "Emotionale Dysbalance": 10 - emotionalBalance,
    "Haut-Imbalance": 10 - skinHealth,
    "Immunschwäche": 10 - immunity,
  };
  const dominantImbalance = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];

  // Saisonaler Faktor
  const seasonEffect = getAnswer("ci_season_effect")?.value;
  const seasonalFactor = seasonEffect === "very_positive" ? "positiv" : seasonEffect === "neutral" ? "neutral" : seasonEffect === "challenging" ? "herausfordernd" : "schwierig";

  // Zusammenfassung
  const summaryParts: string[] = [];
  if (energy <= 4) summaryParts.push("niedrige Energie");
  if (digestion <= 4) summaryParts.push("Verdauungsprobleme");
  if (sleep <= 4) summaryParts.push("Schlafprobleme");
  if (stress >= 7) summaryParts.push("hohes Stresslevel");
  if (emotionalBalance <= 4) summaryParts.push("emotionale Dysbalance");
  if (skinHealth <= 4) summaryParts.push("Hautprobleme");
  if (immunity <= 4) summaryParts.push("geschwächtes Immunsystem");

  const summary = summaryParts.length > 0
    ? `Aktuelle Herausforderungen: ${summaryParts.join(", ")}. Dominante Imbalance: ${dominantImbalance}.`
    : `Insgesamt guter Zustand. Keine gravierenden Imbalancen erkannt.`;

  return {
    energy: Math.round(energy * 10) / 10,
    digestion: Math.round(digestion * 10) / 10,
    sleep: Math.round(sleep * 10) / 10,
    stress: Math.round(stress * 10) / 10,
    emotionalBalance: Math.round(emotionalBalance * 10) / 10,
    skinHealth,
    immunity,
    dominantImbalance,
    seasonalFactor,
    summary,
    capturedAt: Date.now(),
  };
}

/** Prüft ob ein Check-In fällig ist (>= 90 Tage seit dem letzten) */
export function isCheckInDue(lastCheckInAt: Date | null): boolean {
  if (!lastCheckInAt) return false; // Erst nach Onboarding
  const daysSinceLastCheckIn = (Date.now() - lastCheckInAt.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceLastCheckIn >= 90;
}

/** Gibt die Anzahl der Tage seit dem letzten Check-In zurück */
export function daysSinceCheckIn(lastCheckInAt: Date | null): number | null {
  if (!lastCheckInAt) return null;
  return Math.floor((Date.now() - lastCheckInAt.getTime()) / (1000 * 60 * 60 * 24));
}
